/** @module @liquidefy/code-editor/editor/editor @version 1.0.0 @license LQD Research LLC */

// ═══ §1 IMPORTS ═══

import type {
  EditorState,
  EditorMode,
  FocusState,
  ReadOnlyMode,
  EditorConfig,
  ViewportState,
  CursorState,
  CursorGroup,
  DiffChange,
  DiffEditorState,
  LinkedEditorState,
  DocumentId,
  EditorInstanceId,
  EditorResult,
} from '../code-editor-types.js';

import {
  ok,
  err,
  asEditorInstanceId,
  asViewportId,
  asCursorGroupId,
  asDocumentId,
  generateId,
  emptyCursorGroup,
  emptyViewportState,
  DEFAULT_EDITOR_CONFIG,
} from '../code-editor.js';

// ═══ §2 EDITOR CREATION — create, derive, reset, snapshot ═══

/**
 * Creates a new editor state with full defaults.
 */
export function createEditor(
  id: EditorInstanceId,
  documentId: DocumentId,
  config?: Partial<EditorConfig>,
): EditorResult<EditorState> {
  const mergedConfig: EditorConfig = config
    ? { ...DEFAULT_EDITOR_CONFIG, ...config }
    : DEFAULT_EDITOR_CONFIG;

  const state: EditorState = {
    id,
    documentId,
    cursorGroup: emptyCursorGroup(asCursorGroupId(generateId('cg'))),
    viewport: emptyViewportState(asViewportId(generateId('vp'))),
    mode: 'normal',
    focus: { hasFocus: false, focusTarget: 'none' },
    readOnly: 'off',
    config: mergedConfig,
  };

  return ok(state, 'EDITOR_CREATED', `Editor ${id} created`);
}

/**
 * Derives a new editor from an existing one, sharing configuration but with a new ID and document.
 */
export function deriveEditorFromExisting(
  existing: EditorState,
  newId: EditorInstanceId,
  documentId: DocumentId,
): EditorResult<EditorState> {
  const state: EditorState = {
    id: newId,
    documentId,
    cursorGroup: emptyCursorGroup(asCursorGroupId(generateId('cg'))),
    viewport: emptyViewportState(asViewportId(generateId('vp'))),
    mode: existing.mode,
    focus: { hasFocus: false, focusTarget: 'none' },
    readOnly: existing.readOnly,
    config: existing.config,
  };

  return ok(state, 'EDITOR_DERIVED', `Editor ${newId} derived from ${existing.id}`);
}

/**
 * Resets transient editor state (cursors, viewport, focus) while preserving config and document binding.
 */
export function resetEditorState(state: EditorState): EditorResult<EditorState> {
  const reset: EditorState = {
    ...state,
    cursorGroup: emptyCursorGroup(asCursorGroupId(generateId('cg'))),
    viewport: emptyViewportState(asViewportId(generateId('vp'))),
    mode: 'normal',
    focus: { hasFocus: false, focusTarget: 'none' },
  };

  return ok(reset, 'EDITOR_RESET', `Editor ${state.id} state reset`);
}

/**
 * Creates a serializable snapshot of the editor state.
 * Strips any non-serializable data.
 */
export function snapshotEditorState(
  state: EditorState,
): EditorResult<{
  readonly id: string;
  readonly documentId: string;
  readonly mode: EditorMode;
  readonly readOnly: ReadOnlyMode;
  readonly viewport: ViewportState;
  readonly cursorGroup: CursorGroup;
}> {
  return ok({
    id: state.id as string,
    documentId: state.documentId as string,
    mode: state.mode,
    readOnly: state.readOnly,
    viewport: state.viewport,
    cursorGroup: state.cursorGroup,
  }, 'EDITOR_SNAPSHOT', 'Editor state snapshot created');
}

// ═══ §3 MODE MANAGEMENT — transitions, queries, labels ═══

/**
 * Valid mode transitions from each mode.
 */
export const VALID_MODE_TRANSITIONS: Record<EditorMode, EditorMode[]> = {
  'normal': ['insert', 'visual', 'visual-line', 'visual-block', 'command', 'replace', 'select'],
  'insert': ['normal', 'select'],
  'visual': ['normal', 'visual-line', 'visual-block', 'insert', 'command'],
  'visual-line': ['normal', 'visual', 'visual-block', 'insert', 'command'],
  'visual-block': ['normal', 'visual', 'visual-line', 'insert', 'command'],
  'command': ['normal', 'insert'],
  'replace': ['normal', 'insert'],
  'select': ['normal', 'insert'],
};

/**
 * Sets the editor mode with validation against allowed transitions.
 */
export function setMode(state: EditorState, mode: EditorMode): EditorResult<EditorState> {
  if (state.mode === mode) {
    return ok(state, 'MODE_UNCHANGED', `Already in ${mode} mode`);
  }

  const allowed = VALID_MODE_TRANSITIONS[state.mode];
  if (!allowed || !allowed.includes(mode)) {
    return err(
      'INVALID_TRANSITION',
      `Cannot transition from ${state.mode} to ${mode}`,
    );
  }

  return ok(
    { ...state, mode },
    'MODE_SET',
    `Mode changed from ${state.mode} to ${mode}`,
  );
}

/**
 * Returns true if the editor is in insert mode.
 */
export function isInsertMode(state: EditorState): boolean {
  return state.mode === 'insert';
}

/**
 * Returns true if the editor is in any visual mode.
 */
export function isVisualMode(state: EditorState): boolean {
  return state.mode === 'visual' || state.mode === 'visual-line' || state.mode === 'visual-block';
}

/**
 * Returns true if the editor is in normal mode.
 */
export function isNormalMode(state: EditorState): boolean {
  return state.mode === 'normal';
}

/**
 * Returns true if the editor is in command mode.
 */
export function isCommandMode(state: EditorState): boolean {
  return state.mode === 'command';
}

/**
 * Returns true if the given mode supports editing (text insertion/deletion).
 */
export function modeSupportsEditing(mode: EditorMode): boolean {
  return mode === 'insert' || mode === 'replace';
}

/**
 * Returns true if the given mode supports text selection.
 */
export function modeSupportsSelection(mode: EditorMode): boolean {
  return (
    mode === 'visual' ||
    mode === 'visual-line' ||
    mode === 'visual-block' ||
    mode === 'select' ||
    mode === 'insert'
  );
}

/**
 * Returns a human-readable label for a mode.
 */
export function modeLabel(mode: EditorMode): string {
  const labels: Record<EditorMode, string> = {
    'normal': 'NORMAL',
    'insert': 'INSERT',
    'visual': 'VISUAL',
    'visual-line': 'VISUAL LINE',
    'visual-block': 'VISUAL BLOCK',
    'command': 'COMMAND',
    'replace': 'REPLACE',
    'select': 'SELECT',
  };
  return labels[mode];
}

// ═══ §4 FOCUS & READ-ONLY — focus management, read-only state ═══

/**
 * Sets the focus target of the editor.
 */
export function setFocus(
  state: EditorState,
  target: FocusState['focusTarget'],
): EditorResult<EditorState> {
  return ok(
    { ...state, focus: { hasFocus: true, focusTarget: target } },
    'FOCUS_SET',
    `Focus set to ${target}`,
  );
}

/**
 * Clears focus from the editor.
 */
export function clearFocus(state: EditorState): EditorResult<EditorState> {
  return ok(
    { ...state, focus: { hasFocus: false, focusTarget: 'none' } },
    'FOCUS_CLEARED',
    'Focus cleared',
  );
}

/**
 * Returns true if the editor currently has focus.
 */
export function hasFocus(state: EditorState): boolean {
  return state.focus.hasFocus;
}

/**
 * Sets the read-only mode of the editor.
 */
export function setReadOnly(
  state: EditorState,
  mode: ReadOnlyMode,
): EditorResult<EditorState> {
  return ok(
    { ...state, readOnly: mode },
    'READ_ONLY_SET',
    `Read-only mode set to ${mode}`,
  );
}

/**
 * Returns true if the editor is editable (not read-only and in an editing mode).
 */
export function isEditable(state: EditorState): boolean {
  return state.readOnly === 'off' && modeSupportsEditing(state.mode);
}

/**
 * Returns true if text can be selected (soft read-only allows selection).
 */
export function isSelectable(state: EditorState): boolean {
  return state.readOnly !== 'hard';
}

// ═══ §5 EDITOR LINKING — synchronized editors, scroll/cursor sync ═══

/**
 * Creates a linked editor group from a set of editor IDs.
 */
export function linkEditors(
  editors: EditorInstanceId[],
  config?: { syncScrolling?: boolean; syncCursors?: boolean },
): EditorResult<LinkedEditorState> {
  if (editors.length < 2) {
    return err('LINK_MINIMUM', 'At least 2 editors required for linking');
  }

  const linked: LinkedEditorState = {
    editorIds: [...editors],
    syncScrolling: config?.syncScrolling ?? true,
    syncCursors: config?.syncCursors ?? false,
  };

  return ok(linked, 'EDITORS_LINKED', `Linked ${editors.length} editors`);
}

/**
 * Removes an editor from a linked group.
 */
export function unlinkEditor(
  linked: LinkedEditorState,
  editorId: EditorInstanceId,
): EditorResult<LinkedEditorState> {
  const remaining = linked.editorIds.filter((id) => id !== editorId);

  if (remaining.length < 2) {
    return err('LINK_DISSOLVED', 'Link dissolved: fewer than 2 editors remain');
  }

  return ok(
    { ...linked, editorIds: remaining },
    'EDITOR_UNLINKED',
    `Editor ${editorId} unlinked`,
  );
}

/**
 * Applies a source editor's viewport to all linked editors for synchronized scrolling.
 * Returns a map of editorId to updated viewport.
 */
export function syncScroll(
  linked: LinkedEditorState,
  sourceId: EditorInstanceId,
  viewport: ViewportState,
): EditorResult<Map<EditorInstanceId, ViewportState>> {
  if (!linked.syncScrolling) {
    return ok(new Map(), 'SYNC_DISABLED', 'Scroll syncing is disabled');
  }

  const targets = linked.editorIds.filter((id) => id !== sourceId);
  const result = new Map<EditorInstanceId, ViewportState>();

  for (const targetId of targets) {
    result.set(targetId, {
      ...viewport,
      id: asViewportId(generateId('vp')),
    });
  }

  return ok(result, 'SCROLL_SYNCED', `Synced scroll to ${targets.length} editors`);
}

/**
 * Applies a source editor's cursor state to all linked editors.
 */
export function syncCursors(
  linked: LinkedEditorState,
  sourceId: EditorInstanceId,
  cursors: CursorGroup,
): EditorResult<Map<EditorInstanceId, CursorGroup>> {
  if (!linked.syncCursors) {
    return ok(new Map(), 'SYNC_DISABLED', 'Cursor syncing is disabled');
  }

  const targets = linked.editorIds.filter((id) => id !== sourceId);
  const result = new Map<EditorInstanceId, CursorGroup>();

  for (const targetId of targets) {
    result.set(targetId, {
      ...cursors,
      id: asCursorGroupId(generateId('cg')),
    });
  }

  return ok(result, 'CURSORS_SYNCED', `Synced cursors to ${targets.length} editors`);
}

/**
 * Returns true if the given editor is part of the linked group.
 */
export function isLinked(linked: LinkedEditorState, editorId: EditorInstanceId): boolean {
  return linked.editorIds.includes(editorId);
}

/**
 * Returns all editor IDs linked with the given editor (excluding itself).
 */
export function getLinkedEditors(
  linked: LinkedEditorState,
  editorId: EditorInstanceId,
): EditorInstanceId[] {
  return linked.editorIds.filter((id) => id !== editorId);
}

// ═══ §6 DIFF EDITOR — create diff state, navigate changes, toggle layout ═══

/**
 * Creates a diff editor state for comparing two documents.
 */
export function createDiffEditor(
  originalId: DocumentId,
  modifiedId: DocumentId,
  options?: {
    renderSideBySide?: boolean;
    ignoreTrimWhitespace?: boolean;
    maxComputationTime?: number;
  },
): EditorResult<DiffEditorState> {
  const state: DiffEditorState = {
    originalId,
    modifiedId,
    changes: [],
    renderSideBySide: options?.renderSideBySide ?? true,
    ignoreTrimWhitespace: options?.ignoreTrimWhitespace ?? true,
    maxComputationTime: options?.maxComputationTime ?? 5000,
  };

  return ok(state, 'DIFF_EDITOR_CREATED', 'Diff editor created');
}

/**
 * Updates the diff changes in the diff editor state.
 */
export function updateDiffChanges(
  state: DiffEditorState,
  changes: DiffChange[],
): EditorResult<DiffEditorState> {
  return ok(
    { ...state, changes },
    'DIFF_CHANGES_UPDATED',
    `Updated with ${changes.length} diff changes`,
  );
}

/**
 * Toggles between side-by-side and inline diff layout.
 */
export function toggleDiffSideBySide(state: DiffEditorState): EditorResult<DiffEditorState> {
  return ok(
    { ...state, renderSideBySide: !state.renderSideBySide },
    'DIFF_LAYOUT_TOGGLED',
    `Diff layout: ${!state.renderSideBySide ? 'side-by-side' : 'inline'}`,
  );
}

/**
 * Finds the next diff change after the given line.
 */
export function nextDiffChange(
  state: DiffEditorState,
  currentLine: number,
): EditorResult<DiffChange | null> {
  const sorted = [...state.changes].sort(
    (a, b) => a.modifiedStartLineNumber - b.modifiedStartLineNumber,
  );

  const next = sorted.find((c) => c.modifiedStartLineNumber > currentLine);
  if (!next) {
    // Wrap around
    const first = sorted[0] ?? null;
    return ok(first, 'DIFF_NEXT_WRAP', first ? 'Wrapped to first diff' : 'No diffs');
  }

  return ok(next, 'DIFF_NEXT', `Next diff at line ${next.modifiedStartLineNumber}`);
}

/**
 * Finds the previous diff change before the given line.
 */
export function previousDiffChange(
  state: DiffEditorState,
  currentLine: number,
): EditorResult<DiffChange | null> {
  const sorted = [...state.changes].sort(
    (a, b) => b.modifiedStartLineNumber - a.modifiedStartLineNumber,
  );

  const prev = sorted.find((c) => c.modifiedStartLineNumber < currentLine);
  if (!prev) {
    // Wrap around
    const last = sorted[0] ?? null;
    return ok(last, 'DIFF_PREV_WRAP', last ? 'Wrapped to last diff' : 'No diffs');
  }

  return ok(prev, 'DIFF_PREV', `Previous diff at line ${prev.modifiedStartLineNumber}`);
}

/**
 * Returns the diff change that contains the given line, or null if none.
 */
export function diffChangeAtLine(
  state: DiffEditorState,
  line: number,
): DiffChange | null {
  return state.changes.find(
    (c) => line >= c.modifiedStartLineNumber && line <= c.modifiedEndLineNumber,
  ) ?? null;
}
